import type { RelationWithoutHydration } from '../../lib/flotiq-client/types/api-response';
import type { RelationShowFn } from '../RelationCard/RelationCard';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useFeaturedImages } from '../../hooks/api/useFeaturedImages';
import { useRelations } from '../../hooks/api/useRelations';

import Pagination from '../Pagination/Pagination';
import RelationCard from '../RelationCard/RelationCard';

interface RelationModalContentProps {
  /**
   * Data to display in the modal
   */
  relationsRawData?: RelationWithoutHydration[];
  /**
   * Test id for layout
   */
  testId?: string;
}

const HOOK_OPTIONS = {
  staleTime: 10 * 60 * 1000, // 10 minutes
  gcTime: 15 * 60 * 1000, // 15 minutes
};

const RelationModalContent = ({
  relationsRawData = EMPTY_ARRAY,
  testId = '',
}: RelationModalContentProps) => {
  const { t } = useTranslation();
  const [relationsCurrentPage, setRelationsCurrentPage] = useState(1);

  const relationsParams = {
    page: relationsCurrentPage,
    limit: 10,
  };

  const {
    data: relationsData,
    errors: relationsErrors,
    isLoading: relationsAreLoading,
    pagination: relationsPagination,
  } = useRelations(relationsRawData, relationsParams, HOOK_OPTIONS);

  useApiErrorsToast(relationsErrors);

  const contentTypes =
    relationsAreLoading || !relationsData
      ? []
      : Object.values(relationsData).map(({ contentType }) => contentType);

  const [featuredImages, featuredImagesLoading] =
    useFeaturedImages(contentTypes);

  const handleRelationsPageChange = (page: number) => {
    setRelationsCurrentPage(page);
  };

  const openFullSizeImage: RelationShowFn = (media) => {
    window.open(getMediaUrl(media), '_blank');
  };

  return (
    <>
      {!relationsData || Object.keys(relationsData).length <= 0 ? (
        <div
          className={twMerge(
            'flex flex-col items-center space-y-2 font-bold text-lg',
          )}
          {...getTestProps(testId, 'no-relations')}
        >
          {t('ObjectsOfType.NoRelationsData')}
        </div>
      ) : (
        <div
          className={twMerge(
            'flex flex-col items-center space-y-2 px-2 pt-7 pb-5',
          )}
        >
          {relationsData &&
            Object.keys(relationsData).map((contentObjectType) => {
              const relationType = contentObjectType;
              const objects =
                'data' in relationsData[contentObjectType]
                  ? relationsData[contentObjectType].data
                  : relationsData[contentObjectType];
              const titleField =
                'titleField' in relationsData[contentObjectType]
                  ? relationsData[contentObjectType].titleField
                  : '';
              return objects.map((relation) => {
                const cardData =
                  typeof relation === 'string' ? { id: relation } : relation;
                return (
                  <RelationCard
                    key={cardData.id}
                    relation={cardData}
                    relationType={relationType}
                    featuredImageUrl={featuredImages[contentObjectType]}
                    featuredImageLoading={featuredImagesLoading}
                    titleField={titleField}
                    relationsAreLoading={relationsAreLoading}
                    onShow={openFullSizeImage}
                    testId={testId}
                    withObjectStatus
                  />
                );
              });
            })}
        </div>
      )}

      {relationsPagination?.total_pages > 1 && (
        <Pagination
          page={relationsCurrentPage}
          numOfPages={relationsPagination?.total_pages}
          onPageChange={handleRelationsPageChange}
          additionalClasses="p-0! mt-4"
          {...getTestProps(testId, 'relation-pagination', 'testId')}
        />
      )}
    </>
  );
};

export default RelationModalContent;
